// @flow

import { all, put, takeEvery, call, select } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import config from "@config";
import { dataURLtoFile } from "@utils/file";
import { isFirstCareUser } from "@modules/account/selectors";
import actionCreators from "@modules/service/actionCreator";
import {
  AddAlertAttachment,
  DeleteAlertAttachment,
  DownloadAlertAttachment,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { downloadFile } from "@sagas/utils";

type AlertAddAttachmentRequest = $Exact<{
  clientId: number,
  alertId: number,
  form: Object,
}>;

export function* onAddAlertAttachment({
  payload,
}: Action<AlertAddAttachmentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.alertId || !payload.form) {
      throw Error("Invalid payload provided");
    }

    const file = dataURLtoFile(payload.form.file);
    const data = new FormData();
    data.append("file", file.blob, decodeURIComponent(file.name));

    yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/draft/file`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/submit-for-review`,
        {
          notes: "Alert attached added and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.AddAlertAttachmentSuccess(data)),
      put(actionCreators.SetShowAddAlertAttachmentModal(false)),
      put(actionCreators.GetAlert({ clientId: payload.clientId, alertId: payload.alertId })),
      put(sendSuccessMessage("ALERT_ATTACHMENT_ADDED")),
    ]);
  } catch (e) {
    yield put(actionCreators.AddAlertAttachmentError(e.message));
  }
}

type AlertDeleteAttachmentRequest = $Exact<{
  clientId: number,
  alertId: number,
  attachmentId: number,
}>;

export function* onDeleteAlertAttachment({
  payload,
}: Action<AlertDeleteAttachmentRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.alertId || !payload.attachmentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/draft/file/${payload.attachmentId}`,
    );

    const firstCareUser = yield select(isFirstCareUser);
    if (firstCareUser) {
      yield call(
        http.post,
        `/api/v1/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/submit-for-review`,
        {
          notes: "Alert attachment removed and submitted for review",
        },
      );
    }

    yield all([
      put(actionCreators.DeleteAlertAttachmentSuccess(response.data)),
      put(actionCreators.SetShowDeleteAlertAttachmentModal(false)),
      put(actionCreators.GetAlert({ clientId: payload.clientId, alertId: payload.alertId })),
      put(sendSuccessMessage("ALERT_ATTACHMENT_REMOVED")),
    ]);
  } catch (e) {
    yield put(actionCreators.DeleteAlertAttachmentError(e.message));
  }
}

export function* onDownloadAlertAttachment({
  payload,
}: Action<{ clientId: number, alertId: number, fileId: number, fileName: string }>): Generator<
  *,
  void,
  *,
> {
  try {
    if (!payload || !payload.clientId || !payload.alertId || !payload.fileId || !payload.fileName) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v2/service-admin/client/${payload.clientId}/service-config/absence-management/alert/configuration/${payload.alertId}/file/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadFile(response.data, `${payload.fileId} ${payload.fileName}`);

    yield all([put(actionCreators.DownloadAlertAttachmentSuccess(response.data))]);
  } catch (e) {
    yield put(actionCreators.DownloadAlertAttachmentError(e.response));
  }
}

// ROOT

export function* AlertAttachmentsSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(AddAlertAttachment, onAddAlertAttachment),
    takeEvery(DeleteAlertAttachment, onDeleteAlertAttachment),
    takeEvery(DownloadAlertAttachment, onDownloadAlertAttachment),
  ]);
}

export default AlertAttachmentsSagas;
